import React, { useCallback, useMemo, useState } from "react";
import AccountDrawer from "./AccountDrawer";
import AccountFilters from "./AccountFilters";
import AccountStatusBadge from "./AccountStatusBadge";
import MerchantProfileCompleteness from "./MerchantProfileCompleteness";
import ProtectedProfileImage from "./ProtectedProfileImage";
import ResponsiveAccountTable from "./ResponsiveAccountTable";
import UserManagementDateTime from "./UserManagementDateTime";
import UserManagementHeader from "./UserManagementHeader";
import UserManagementPagination from "./UserManagementPagination";
import useAccountList from "./useAccountList";
import { getUserManagementText } from "./translations";
import "../../css/UserManagement.css";
import "../../css/RegistrationReviews.css";

const locationName = (row, language) => {
  const governorate = row.location?.city || row.governorate;
  const suffix = language === "ar" ? "Ar" : "En";
  const governorateName = governorate?.[`name${suffix}`] || governorate?.name;
  const country = row.location?.country || governorate?.country;
  const countryName = country?.[`name${suffix}`] || country?.name;
  return [governorateName, countryName]
    .filter(Boolean)
    .join(language === "ar" ? "، " : ", ") || "—";
};

const AccountPerson = ({ type, row, name, subtitle, text }) => (
  <span className="registration-review-person account-directory-person">
    <ProtectedProfileImage
      type={type}
      accountId={row._id || row.id}
      available={Boolean(row.profileImageAvailable || row.image || row.logo)}
      name={name}
      imageLabel={text.profileImage}
      className="registration-review-person-image"
    />
    <span className="registration-review-person-copy">
      <strong>{name || "—"}</strong>
      {subtitle && subtitle !== name && <small>{subtitle}</small>}
    </span>
  </span>
);

const AccountListPage = ({ type, language = "en", direction = "ltr" }) => {
  const text = getUserManagementText(language);
  const list = useAccountList(type);
  const [selected, setSelected] = useState(null);
  const isCustomer = type === "customers";
  const statusLabel = useCallback(
    (row) => row.isBlocked
      ? text.blocked
      : row.lifecycleState === "DELETED"
        ? text.deleted
        : text.active,
    [text.active, text.blocked, text.deleted]
  );
  const closeDrawer = useCallback(() => setSelected(null), []);

  const columns = useMemo(() => isCustomer ? [
    {
      key: "fullName",
      label: text.customerName,
      render: (row) => <AccountPerson type="customers" row={row} name={row.fullName} text={text} />
    },
    { key: "email", label: text.email },
    { key: "phone", label: text.phone },
    { key: "location", label: text.location, render: (row) => locationName(row, language) },
    { key: "registeredAt", label: text.registrationDate, render: (row) => <UserManagementDateTime value={row.registeredAt} language={language} /> },
    { key: "state", label: text.status, render: (row) => <AccountStatusBadge status={row.lifecycleState} label={statusLabel(row)} /> }
  ] : [
    {
      key: "storeName",
      label: text.storeName,
      render: (row) => <AccountPerson
        type="merchants"
        row={row}
        name={language === "ar" ? row.nameAr || row.nameEn : row.nameEn || row.nameAr}
        subtitle={language === "ar" ? row.nameEn : row.nameAr}
        text={text}
      />
    },
    { key: "ownerFullLegalName", label: text.ownerName },
    { key: "email", label: text.email },
    { key: "phone", label: text.phone },
    { key: "category", label: text.category, render: (row) => row.category?.[`name${language === "ar" ? "Ar" : "En"}`] || row.category?.name || "—" },
    { key: "profileCompleteness", label: text.profileCompleteness, render: (row) => <MerchantProfileCompleteness merchant={row} text={text} compact /> },
    { key: "registeredAt", label: text.registrationDate, render: (row) => <UserManagementDateTime value={row.registeredAt} language={language} /> },
    { key: "state", label: text.status, render: (row) => <AccountStatusBadge status={row.lifecycleState} label={statusLabel(row)} /> }
  ], [isCustomer, language, statusLabel, text]);

  return (
    <main className="user-management-page account-directory-page" dir={direction}>
      <UserManagementHeader
        eyebrow={text.navigation.parent}
        title={isCustomer ? text.customers : text.merchants}
        total={list.pagination.totalResults || 0}
        totalLabel={text.total}
        refreshLabel={text.refresh}
        onRefresh={list.refresh}
        refreshing={list.loading}
      />
      <section className="account-list-card">
        <AccountFilters
          type={type}
          filters={list.filters}
          searchDraft={list.searchDraft}
          onSearchChange={list.setSearchDraft}
          onChange={list.updateFilters}
          onClear={list.clearFilters}
          text={text}
          language={language}
        />
        <ResponsiveAccountTable
          columns={columns}
          rows={list.rows}
          loading={list.loading}
          onOpen={setSelected}
          actionLabel={text.viewDetails}
          emptyLabel={isCustomer ? text.noCustomers : text.noMerchants}
          emptyHint={list.hasActiveFilters ? text.noFilterMatchesHint : text.noApprovedAccountsHint}
          tableClassName={`account-directory-table is-${type}`}
        />
        <UserManagementPagination
          page={list.pagination.currentPage || list.filters.page}
          totalPages={list.pagination.totalPages || 1}
          perPage={list.filters.perPage || 10}
          onPageChange={(page) => list.updateFilters({ page })}
          onPerPageChange={(perPage) => list.updateFilters({ perPage, page: 1 })}
          rowsPerPageLabel={text.rowsPerPage}
          pageLabel={text.page}
          previousLabel={text.previous}
          nextLabel={text.next}
          direction={direction}
        />
      </section>
      <AccountDrawer type={type} selected={selected} show={Boolean(selected)} onClose={closeDrawer} onChanged={list.refresh} text={text} direction={direction} language={language} />
    </main>
  );
};

export default AccountListPage;
